import React, { useEffect, useMemo, useRef, useState } from "react";
import { computeMonthlyEvolution } from "../utils/paymentEvolution";
import { formatCurrencyUY } from "../utils/formatters";
import { useIsMobile } from "../hooks/useResponsive";
import { appColors, appType } from "../utils/appTheme";
import { SegmentedControl } from "./app";
import { useTranslation } from "react-i18next";

const PAID_COLOR = appColors.blue; // azul = pagado
const PENDING_COLOR = appColors.red; // rojo = pendiente

/**
 * Gráfico de evolución de pagos: UNA barra apilada por mes (azul = pagado,
 * rojo = pendiente) con selector 6 / 12 / 18 meses.
 *
 * Reutilizable por el estado de cuenta del residente ("Mi cuenta") y por la
 * vista del edificio (`BuildingAccountView`). Es card-agnóstico: el caller lo
 * envuelve en la card/section que corresponda.
 *
 * Props:
 *  - payments: pagos a graficar (de un residente o de todo el edificio).
 *  - parseAmount?: parser de montos (por defecto Number).
 *  - initialMonths?: rango inicial (6 por defecto).
 *  - title?: título (por defecto "Evolución de pagos").
 *  - selectable?: si true, cada barra es clickeable y notifica onSelectMonth.
 *  - onSelectMonth?: (item, index) => void — item = {month,year,label,paid,pending,total,key}.
 */
const PaymentEvolutionChart = ({
  payments = [],
  parseAmount,
  initialMonths = 6,
  title,
  selectable = false,
  onSelectMonth,
}) => {
  const { t } = useTranslation("paymentChart");
  const RANGE_OPTIONS = [
    { value: 6, label: t("range.m6") },
    { value: 12, label: t("range.m12") },
    { value: 18, label: t("range.m18") },
  ];
  // title === null → sin título (lo pone el caller). undefined → título por defecto.
  const resolvedTitle = title === null ? null : (title || t("title"));
  const isMobile = useIsMobile();
  const [months, setMonths] = useState(initialMonths);
  const [selIndex, setSelIndex] = useState(initialMonths - 1);

  const series = useMemo(
    () => computeMonthlyEvolution({ payments, months, parseAmount }),
    [payments, months, parseAmount]
  );

  const maxTotal = useMemo(() => Math.max(...series.map((m) => m.total), 1), [series]);
  const hasData = useMemo(() => series.some((m) => m.total > 0), [series]);
  const clampedSel = Math.min(Math.max(selIndex, 0), series.length - 1);

  // Notificar la selección hacia arriba (para el panel de composición desktop).
  const onSelectRef = useRef(onSelectMonth);
  onSelectRef.current = onSelectMonth;
  useEffect(() => {
    if (selectable && series[clampedSel]) onSelectRef.current?.(series[clampedSel], clampedSel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clampedSel, months, selectable]);

  const handleRange = (m) => {
    setMonths(m);
    setSelIndex(m - 1); // al cambiar el rango, seleccionar el mes más reciente
  };

  const barH = isMobile ? 150 : 160;
  // Con más meses, barras más finas y sin monto encima (se lee por color/tooltip).
  const showAmountOnTop = selectable && !isMobile && months <= 6;
  const labelSize = months >= 12 ? 8.5 : isMobile ? 10 : 10.5;

  return (
    <div>
      {/* Header: título + selector de rango */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: resolvedTitle ? "space-between" : "flex-end",
          gap: 10,
          flexWrap: "wrap",
          marginBottom: 12,
        }}
      >
        {resolvedTitle ? <span style={{ ...appType.cardTitle, minWidth: 0 }}>{resolvedTitle}</span> : null}
        <div style={{ maxWidth: 260, flex: "1 1 200px" }}>
          <SegmentedControl options={RANGE_OPTIONS} value={months} onChange={handleRange} />
        </div>
      </div>

      {/* Leyenda */}
      <div style={{ display: "flex", gap: 16, marginBottom: 12 }}>
        <LegendItem color={PAID_COLOR} label={t("paid")} />
        <LegendItem color={PENDING_COLOR} label={t("pending")} />
      </div>

      {hasData ? (
        <>
          {/* Barras */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              gap: months >= 12 ? 3 : 6,
              height: barH,
              borderBottom: `1px solid ${appColors.line}`,
              paddingBottom: 1,
            }}
          >
            {series.map((m, index) => {
              const isSel = index === clampedSel;
              const totalH = m.total > 0 ? Math.max(Math.round((m.total / maxTotal) * (barH - 22)), 6) : 0;
              const paidPct = m.total > 0 ? (m.paid / m.total) * 100 : 0;
              const pendPct = m.total > 0 ? (m.pending / m.total) * 100 : 0;
              const dim = selectable && !isSel;

              return (
                <div
                  key={m.key}
                  onClick={selectable ? () => setSelIndex(index) : undefined}
                  title={t("tooltip", { label: m.label, year: m.year, paid: formatCurrencyUY(m.paid), pending: formatCurrencyUY(m.pending) })}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    height: "100%",
                    cursor: selectable ? "pointer" : "default",
                  }}
                >
                  {showAmountOnTop && m.total > 0 && (
                    <span
                      style={{
                        fontSize: 9,
                        color: isSel ? appColors.ink2 : appColors.ink3,
                        whiteSpace: "nowrap",
                        marginBottom: 3,
                      }}
                    >
                      {formatCurrencyUY(m.total)}
                    </span>
                  )}
                  {/* Barra apilada: rojo (pendiente) arriba, azul (pagado) abajo */}
                  <div
                    style={{
                      width: isSel ? 30 : months >= 12 ? "78%" : 24,
                      maxWidth: 34,
                      height: totalH,
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: "4px 4px 0 0",
                      overflow: "hidden",
                      opacity: dim ? 0.5 : 1,
                      transition: "opacity .15s ease, width .15s ease",
                    }}
                  >
                    {m.pending > 0 && <div style={{ height: `${pendPct}%`, background: PENDING_COLOR }} />}
                    {m.paid > 0 && <div style={{ height: `${paidPct}%`, background: PAID_COLOR }} />}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Labels de meses */}
          <div style={{ display: "flex", gap: months >= 12 ? 3 : 6, marginTop: 5 }}>
            {series.map((m, index) => {
              const isSel = index === clampedSel;
              const showYear = m.month === 0 || index === 0;
              return (
                <div
                  key={m.key}
                  onClick={selectable ? () => setSelIndex(index) : undefined}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    textAlign: "center",
                    cursor: selectable ? "pointer" : "default",
                  }}
                >
                  <span
                    style={{
                      fontSize: labelSize,
                      color: isSel ? appColors.ink : appColors.ink3,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {m.label}
                  </span>
                  {showYear && (
                    <div style={{ fontSize: 8, color: appColors.ink3 }}>{String(m.year).slice(2)}</div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div
          style={{
            ...appType.bodySm,
            color: appColors.ink3,
            textAlign: "center",
            padding: "28px 8px",
          }}
        >
          {t("empty")}
        </div>
      )}
    </div>
  );
};

const LegendItem = ({ color, label }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
    <span style={{ width: 10, height: 10, borderRadius: 3, background: color }} />
    <span style={{ ...appType.caption }}>{label}</span>
  </span>
);

export default PaymentEvolutionChart;
